import { useEffect, useState } from "react";
import { BadgeCheck, PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Overline, Stars, AddToCartButton } from "@/components/common/ui";
import { ReviewForm } from "@/components/home/ReviewForm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolve = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [visible, setVisible] = useState(12);
  const [formOpen, setFormOpen] = useState(false);
  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="reviews-page" className="bg-[#FFFCFA]">
      <header className="border-b border-[#F2E8E6] bg-white py-20 text-center md:py-28">
        <Overline>Real Glow Stories</Overline>
        <h1 className="mt-4 font-display text-5xl font-light text-[#2C2B2C] sm:text-7xl">Loved by thousands</h1>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Stars n={5} className="h-6 w-6" />
          <span className="font-display text-2xl text-[#2C2B2C]">4.9</span>
          <span className="text-[#8A7F86]">/ 5 · 50+ verified reviews</span>
        </div>
        <button onClick={() => setFormOpen(true)} data-testid="open-review-form"
          className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#2C2B2C] px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#2C2B2C] transition-all hover:bg-[#2C2B2C] hover:text-[#FFFCFA]">
          <PenLine className="h-4 w-4" /> Write a Review
        </button>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12">
        <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 xl:columns-4 [&>*]:mb-6">
          {reviews.slice(0, visible).map((r, i) => (
            <Reveal key={r.id || i} delay={(i % 4) * 0.05} className="break-inside-avoid overflow-hidden rounded-2xl border border-[#F2E8E6] bg-white">
              {r.photo && <img src={resolve(r.photo)} alt={`${r.name}'s result`} loading="lazy" className="aspect-square w-full object-cover" />}
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <Stars n={r.rating} className="h-4 w-4" />
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 text-[0.6rem] font-semibold uppercase tracking-wider text-[#3B82F6]">
                      <BadgeCheck className="h-3.5 w-3.5" /> Verified Customer
                    </span>
                  )}
                </div>
                <p className="mt-4 font-display text-lg text-[#2C2B2C]">"{r.title}"</p>
                <p className="mt-2 text-sm font-light leading-relaxed text-[#8A7F86]">{r.text}</p>
                <div className="mt-5 border-t border-[#F2E8E6] pt-4">
                  <span className="text-sm font-medium text-[#2C2B2C]">{r.name}</span>
                  <span className="ml-2 text-xs text-[#8A7F86]">{[r.location, r.days].filter(Boolean).join(" · ")}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        {visible < reviews.length && (
          <div className="mt-12 text-center">
            <button onClick={() => setVisible((v) => v + 12)} data-testid="show-more-reviews"
              className="inline-flex items-center gap-2 rounded-full bg-[#232222] px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-[#FFFCFA] transition-all hover:bg-[#4A2A38]">
              Show More Reviews
            </button>
          </div>
        )}
        <div className="mt-16 text-center">
          <AddToCartButton sku="single" label="Start Your Glow Story" testid="reviews-add" />
        </div>
      </div>

      <ReviewForm open={formOpen} onClose={() => setFormOpen(false)} onSubmitted={(rev) => setReviews((prev) => [rev, ...prev])} />
    </div>
  );
}
